interface HlsInstance {
    on(
        event: string,
        callback: (event: string, data: { fatal?: boolean }) => void
    ): void;
    loadSource(src: string): void;
    attachMedia(video: HTMLVideoElement): void;
    destroy(): void;
}

interface HlsStatic {
    new (): HlsInstance;
    isSupported(): boolean;
    Events: { ERROR: string };
}

declare global {
    interface Window {
        Hls?: HlsStatic;
    }
}

export class HlsVideo {
    constructor() {
        const videos =
            document.querySelectorAll<HTMLVideoElement>("video[data-hls]");
        videos.forEach((video) => this.setup(video));
    }

    private setup(video: HTMLVideoElement): void {
        const src = video.dataset.hls;
        if (!src) {
            return;
        }

        const block = video.closest<HTMLElement>(".verderliquids-keyvisual");
        const toggle = block
            ? block.querySelector<HTMLButtonElement>(
                  ".verderliquids-keyvisual-video-toggle"
              )
            : null;

        // WCAG: never autoplay for users who prefer reduced motion.
        const reducedMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;
        if (reducedMotion) {
            video.removeAttribute("autoplay");
        }

        video.addEventListener("error", () => this.disable(toggle));

        if (window.Hls && window.Hls.isSupported()) {
            const hls = new window.Hls();
            hls.on(window.Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    hls.destroy();
                    this.disable(toggle);
                }
            });
            hls.loadSource(src);
            hls.attachMedia(video);
        } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
            // Chrome reports "maybe" here despite having no native HLS demuxer, but
            // Hls.isSupported() is false first, so this path only runs for browsers
            // (Safari) whose <video> engine genuinely plays .m3u8 without hls.js.
            video.src = src;
        } else {
            this.disable(toggle);
            return;
        }

        if (toggle) {
            this.wireToggle(video, toggle);
        }
    }

    private wireToggle(
        video: HTMLVideoElement,
        toggle: HTMLButtonElement
    ): void {
        const pauseIcon = toggle.querySelector(".toggle-icon-pause");
        const playIcon = toggle.querySelector(".toggle-icon-play");

        const sync = () => {
            const playing = !video.paused;
            const label = playing
                ? toggle.dataset.labelPause
                : toggle.dataset.labelPlay;
            if (label) {
                toggle.setAttribute("aria-label", label);
            }
            if (pauseIcon) {
                pauseIcon.classList.toggle("hidden", !playing);
            }
            if (playIcon) {
                playIcon.classList.toggle("hidden", playing);
            }
        };

        toggle.addEventListener("click", () => {
            if (video.paused) {
                video.play().catch((error) => {
                    if (error && error.name === "AbortError") {
                        return;
                    }
                    this.disable(toggle);
                });
            } else {
                video.pause();
            }
        });

        video.addEventListener("play", sync);
        video.addEventListener("pause", sync);

        toggle.hidden = false;
        sync();
    }

    private disable(toggle: HTMLButtonElement | null): void {
        if (toggle) {
            toggle.hidden = true;
        }
    }
}
